import {
  ButtonGroup,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';

import DevSandboxTooltip from './DevSandboxTooltip';
import { TEditorLayout } from './types';
import useSandboxTheme from './useSandboxTheme';

// Layout icons
const LayoutLeftRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="12" y1="3" x2="12" y2="21" />
  </svg>
);

const LayoutRightLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="12" y1="3" x2="12" y2="21" />
    <rect x="13" y="4" width="7" height="16" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

const LayoutTopBottomIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="12" x2="21" y2="12" />
  </svg>
);

const PreviewOnlyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
  </svg>
);

// Current layout icon component
const CurrentLayoutIcon = ({ layout }: { layout: TEditorLayout }) => {
  switch (layout) {
    case 'left-right':
      return <LayoutLeftRightIcon />;
    case 'right-left':
      return <LayoutRightLeftIcon />;
    case 'top-bottom':
      return <LayoutTopBottomIcon />;
    case 'preview-only':
      return <PreviewOnlyIcon />;
    default:
      return <LayoutLeftRightIcon />;
  }
};

interface ILayoutButtonProps {
  layout: TEditorLayout;
  onLayoutChange: (layout: TEditorLayout) => void;
  /** When true, only show left-right and right-left options (no top-bottom or preview-only) */
  horizontalOnly?: boolean;
}

const ALL_LAYOUT_OPTIONS: { value: TEditorLayout; icon: React.ReactElement; label: string }[] = [
  { value: 'left-right', icon: <LayoutLeftRightIcon />, label: 'Editor | Preview' },
  { value: 'right-left', icon: <LayoutRightLeftIcon />, label: 'Preview | Editor' },
  { value: 'top-bottom', icon: <LayoutTopBottomIcon />, label: 'Editor / Preview' },
  { value: 'preview-only', icon: <PreviewOnlyIcon />, label: 'Preview Only' },
];

const HORIZONTAL_ONLY_OPTIONS = ALL_LAYOUT_OPTIONS.filter(
  (o) => o.value === 'left-right' || o.value === 'right-left'
);

const LayoutButton = ({ layout, onLayoutChange, horizontalOnly }: ILayoutButtonProps) => {
  const layoutOptions = horizontalOnly ? HORIZONTAL_ONLY_OPTIONS : ALL_LAYOUT_OPTIONS;
  const { colors } = useSandboxTheme();

  return (
    <Popover placement="bottom-end" trigger="click">
      <DevSandboxTooltip label="Editor layout">
        <span>
          <PopoverTrigger>
            <IconButton
              aria-label="Change layout"
              icon={<CurrentLayoutIcon layout={layout} />}
              size="md"
              variant="outline"
              borderColor={colors.controlBorder}
              color={colors.textSecondary}
              bg={colors.controlBg}
              _hover={{ color: colors.textPrimary, bg: colors.hoverBg, borderColor: colors.controlBorderHover }}
              _active={{ bg: colors.activeBg }}
            />
          </PopoverTrigger>
        </span>
      </DevSandboxTooltip>
      <PopoverContent
        bg={colors.popoverBg}
        borderColor={colors.popoverBorder}
        w="auto"
        boxShadow={colors.popoverShadow}
        _focus={{ outline: 'none', boxShadow: colors.popoverShadow }}
      >
        <PopoverArrow bg={colors.popoverBg} boxShadow="none" />
        <PopoverBody py={4} px={4}>
          <Text
            fontSize="micro"
            color={colors.sectionLabel}
            fontWeight="semibold"
            letterSpacing="0.05em"
            textTransform="uppercase"
            mb={3}
          >
            Layout
          </Text>
          <ButtonGroup size="md" isAttached variant="outline">
            {layoutOptions.map((option) => (
              <IconButton
                key={option.value}
                aria-label={option.label}
                icon={option.icon}
                onClick={() => onLayoutChange(option.value)}
                bg={layout === option.value ? 'purple.500' : 'transparent'}
                borderColor={layout === option.value ? 'purple.500' : colors.controlBorder}
                color={layout === option.value ? 'white' : colors.textSecondary}
                _hover={{
                  bg: layout === option.value ? 'purple.400' : colors.hoverBg,
                }}
              />
            ))}
          </ButtonGroup>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default LayoutButton;
